"use client";

import Image from "next/image";
import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { motion } from "framer-motion";

interface JobPost {
  id: number;
  documentId: string;
  title: string;
  excerpt: string;
  employmentType: string;
  locationType: string;
  description: string;
  deadline: string;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
}

interface CurrentOpeningsProps {
  title?: string;
  subtitle?: string;
  jobPosts?: JobPost[];
  lang?: string;
}

interface JobListing {
  title: string;
  type: string;
  location: string;
  postedDate: string;
  description: string;
  deadline: string;
}

function JobCard({
  job,
  lang = "en",
  onMouseEnter,
  onMouseLeave,
  isHovered = false,
}: {
  job: JobPost;
  lang?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isHovered?: boolean;
}) {
  const router = useRouter();
  // Format the deadline date
  const formatDeadline = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Calculate posted date
  const calculatePostedDate = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - created.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `Posted ${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  };

  // Handle Apply button click
  const handleApplyClick = () => {
    router.push(`/${lang}/career/${job.slug}`);
  };

  return (
    <div
      className="bg-white rounded-xl p-4 sm:p-7 shadow-lg hover:shadow-xl transition-shadow duration-300 job-card"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="space-y-4 sm:space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3 sm:gap-0">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-medium text-primary-800 leading-tight tracking-tight line-clamp-1">
            {job.title}
          </h3>
          <div className="flex items-center gap-1 px-3 py-1.5 bg-white border border-green-600 rounded-full self-start sm:self-auto">
            <div className="w-2.5 h-2.5 bg-green-600 rounded-full"></div>
            <span className="text-xs font-medium text-gray-800 whitespace-nowrap">
              {job.employmentType}
            </span>
          </div>
        </div>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-1">
            <svg
              className="w-5 h-5"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.4984 11.1916C11.9344 11.1916 13.0984 10.0275 13.0984 8.59158C13.0984 7.15564 11.9344 5.99158 10.4984 5.99158C9.0625 5.99158 7.89844 7.15564 7.89844 8.59158C7.89844 10.0275 9.0625 11.1916 10.4984 11.1916Z"
                stroke="#3D434C"
                strokeWidth="1.25"
              />
              <path
                d="M3.5148 7.07496C5.15646 -0.141705 15.8481 -0.133372 17.4815 7.08329C18.4398 11.3166 15.8065 14.9 13.4981 17.1166C11.8231 18.7333 9.17313 18.7333 7.4898 17.1166C5.1898 14.9 2.55646 11.3083 3.5148 7.07496Z"
                stroke="#3D434C"
                strokeWidth="1.25"
              />
            </svg>
            <span className="text-sm font-medium text-gray-800">
              {job.locationType}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <svg
              className="w-5 h-5"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.16406 1.66663V4.16663"
                stroke="#3D434C"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.8359 1.66663V4.16663"
                stroke="#3D434C"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M3.41406 7.57495H17.5807"
                stroke="#3D434C"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M18 7.08329V14.1666C18 16.6666 16.75 18.3333 13.8333 18.3333H7.16667C4.25 18.3333 3 16.6666 3 14.1666V7.08329C3 4.58329 4.25 2.91663 7.16667 2.91663H13.8333C16.75 2.91663 18 4.58329 18 7.08329Z"
                stroke="#3D434C"
                strokeWidth="1.25"
                strokeMiterlimit="10"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.5762 11.4167H13.5836"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.5762 13.9167H13.5836"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.498 11.4167H10.5055"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M10.498 13.9167H10.5055"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.41209 11.4167H7.41957"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.41209 13.9167H7.41957"
                stroke="#3D434C"
                strokeWidth="1.66667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm font-medium text-gray-800">
              {calculatePostedDate(job.createdAt)}
            </span>
          </div>
        </div>

        {/* Divider */}
        <div className="h-0.5 bg-gray-50 relative overflow-hidden">
          <div
            className="absolute inset-0 bg-primary-800 transition-all duration-300 ease-in-out"
            style={{ width: isHovered ? "100%" : "0%" }}
          ></div>
        </div>

        {/* Description */}
        <p className="text-gray-900 leading-relaxed line-clamp-3">
          {job.excerpt}
        </p>

        {/* Bottom Divider */}
        <div className="h-px bg-gray-50"></div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-0">
          <div className="flex items-center gap-1">
            <span className="font-semibold text-gray-900">Deadline:</span>
            <span className="font-medium text-gray-800">
              {formatDeadline(job.deadline)}
            </span>
          </div>
          <button
            onClick={handleApplyClick}
            className="flex items-center justify-center gap-2.5 px-4 py-3 sm:px-6 sm:py-3 border border-primary-800 text-primary-800 font-medium rounded-full hover:bg-primary-800 hover:text-white transition-colors duration-200 group cursor-pointer w-full sm:w-auto"
          >
            <svg
              className="w-2 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5"
              viewBox="0 0 8 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.91598 7.37487L2.03865 12.2106C1.83065 12.4187 1.49731 12.4187 1.33065 12.2106L1.03865 11.9185C0.989752 11.8735 0.950722 11.8188 0.924023 11.758C0.897324 11.6971 0.883539 11.6314 0.883539 11.565C0.883539 11.4985 0.897324 11.4328 0.924023 11.3719C0.950722 11.3111 0.989752 11.2564 1.03865 11.2114L5.29198 7.00001L1.03865 2.83126C0.989537 2.78627 0.950323 2.73155 0.923495 2.67057C0.896667 2.6096 0.882812 2.5437 0.882812 2.47708C0.882812 2.41046 0.896667 2.34457 0.923495 2.2836C0.950323 2.22262 0.989537 2.1679 1.03865 2.12291L1.33198 1.83076C1.49865 1.62266 1.83198 1.62266 2.03998 1.83076L6.95865 6.66651C7.04277 6.76793 7.08517 6.89757 7.07725 7.02913C7.06933 7.16068 7.01167 7.28429 6.91598 7.37487Z"
                fill="currentColor"
              />
            </svg>
            <span>Apply</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CurrentOpenings({
  title = "Current Openings",
  subtitle = "Explore our latest job opportunities and apply to be part of our growing team.",
  jobPosts = [],
  lang = "en",
}: CurrentOpeningsProps) {
  const [isContainerHovered, setIsContainerHovered] = useState(false);
  const [hoveredJobId, setHoveredJobId] = useState<number | null>(null);

  // Animation variants
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const handleContainerMouseEnter = () => setIsContainerHovered(true);
  const handleContainerMouseLeave = () => setIsContainerHovered(false);

  const handleJobMouseEnter = (jobId: number) => {
    setHoveredJobId(jobId);
    setIsContainerHovered(true);
  };

  const handleJobMouseLeave = () => {
    setHoveredJobId(null);
    setIsContainerHovered(false);
  };

  return (
    <motion.section
      className="mt-25 mx-auto"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={sectionVariants}
    >
      {/* Section Header */}
      <div className="text-center mb-12 lg:mb-[50px]">
        <div className="space-y-4 mb-6">
          <h2 className="text-3xl lg:text-4xl xl:text-5xl font-medium text-gray-900 leading-tight tracking-tight">
            {title}
          </h2>
          <p className="text-lg lg:text-xl text-gray-800 font-medium leading-relaxed max-w-3xl mx-auto tracking-tight">
            {subtitle}
          </p>
        </div>
        <div className="w-12 h-0.5 bg-primary-800 mx-auto"></div>
      </div>

      {/* Job Listings */}
      <div
        className="relative bg-white overflow-hidden container-group group/progress"
        onMouseEnter={handleContainerMouseEnter}
        onMouseLeave={handleContainerMouseLeave}
      >
        {/* Background Pattern */}
        <Image
          src="/images/shared/page-blend-bg.svg"
          alt="Background pattern"
          fill
          className="object-cover object-bottom opacity-30 luminosity mix-blend-luminosity pointer-events-none select-none"
        />

        {/* Content */}
        <div className="relative z-10 p-8 lg:p-12">
          {jobPosts.length > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {jobPosts.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  lang={lang}
                  onMouseEnter={() => handleJobMouseEnter(job.id)}
                  onMouseLeave={handleJobMouseLeave}
                  isHovered={hoveredJobId === job.id}
                />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">
                No job openings available at the moment.
              </p>
            </div>
          )}
        </div>

        {/* Progress Bar */}
        <div className="flex h-[2px] bg-gray-25 relative">
          <div className="absolute left-0 top-0 h-[2px] bg-[#1C8579] w-0 group-hover/progress:w-full transition-all duration-500"></div>
        </div>
      </div>
    </motion.section>
  );
}
